import { setNoOfTravellers, setRawRooms } from 'state/slices/room';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useEffect, useState } from 'react';

import { ChangeDate as ChangeDateModal } from '../../changeDate/ChangeDate';
import { ContinueCta } from 'components/MobileCta/Continue';
import { RoomForm } from './forms';
import { RoomFormat } from 'lib/types/trip';
import { RoomSubmit } from './room-submit';
import { RoomValidation } from './room-validation';
import { SelectionAvailability } from 'lib/types/availability';
import { TRoomSubmit } from 'lib/types/room';
import { FlexibleDepartureDecision } from 'lib/types/product';
import { useDevice } from 'lib/hooks/useDevice';
import { useScrollToStageOnMobile } from 'lib/hooks/useScrollToStageOnMobile';
import { useIsMutating } from '@tanstack/react-query';
import useTripQuery from 'lib/react-query/Queries/useTripQuery';
import { useSessionLockStatus } from '@/lib/react-query/Queries/useSessionLockStatus';
import { useCustomNavigate } from 'lib/hooks/useCustomNavigate';
import { stagePathConfig } from 'data/stage';
import { useTenant } from 'lib/hooks/useTenant';

const RoomStage = ({
  selectedRooms,
  setSelectedRooms,
  submitRoom,
  roomHasError,
  isRoomLoading,
  roomValidationMessage,
  roomValidationKind,
  travellerCountDropdown,
  setTravellerCountDropdown,
  submitRoomIsGotToGo: isGotToGo,
}: TRoomSubmit) => {
  const [isChooseDateOpen, setIsChooseDateOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { isDA } = useTenant();
  const navigate = useCustomNavigate();
  const hasDepartureDate = useAppSelector(
    (state) => state.product.hasDepartureDate
  );
  const roomsState = useAppSelector((state) => state.product.rooms);
  const { isLocked, isLoading: isSessionLockLoading } = useSessionLockStatus();

  const { isMobile } = useDevice();
  // DealsAway mobile only: skip the repeated indicator / trip summary above.
  const stageRef = useScrollToStageOnMobile<HTMLDivElement>();
  const [defaultRooms, setDefaultRooms] = useState<RoomFormat[] | null>(null);

  const { isLoading: tripDataLoading, data: tripData } = useTripQuery();
  const isGotToGoLoading = useIsMutating({ mutationKey: ['got-to-go'] });
  const isGotToGoProcessing =
    !!isGotToGoLoading || (isGotToGo && isRoomLoading);

  const onChangeTravellerDropdown = () => {
    setSelectedRooms([]);
    setDefaultRooms(null);
  };
  const isSubmitRoomLoading = isRoomLoading && !isGotToGo;
  // DealsAway shows the assignment instruction without the "Wait" prefix.
  const hideValidationLabel = isDA && roomValidationKind === 'unassigned';
  const hasMobileInstruction = isDA && isMobile;

  // The DA mobile bar is taller (instruction row above Continue), so the
  // page needs extra bottom padding (see src/styles/layout.css).
  useEffect(() => {
    if (!hasMobileInstruction) return;
    document.body.classList.add('rooms-bar-extended');
    return () => document.body.classList.remove('rooms-bar-extended');
  }, [hasMobileInstruction]);

  const closeChooseDateModal = () => setIsChooseDateOpen(false);

  const checkSubmit = () => {
    if (hasDepartureDate) {
      submitRoom();
    } else {
      setIsChooseDateOpen(true);
    }
  };

  const afterSubmitModal = (
    flexibleDeparture: boolean,
    selectedAvailability: SelectionAvailability,
    flexibleDepartureDecision: FlexibleDepartureDecision | null
  ) => {
    submitRoom(
      flexibleDeparture,
      selectedAvailability,
      flexibleDepartureDecision
    );
  };

  // Locked sessions skip Rooms: go to the stage after Rooms
  // (Traveller on GWAT, Payment on DealsAway).
  useEffect(() => {
    if (!isSessionLockLoading && isLocked) {
      navigate(isDA ? stagePathConfig.payment : stagePathConfig.traveller, {
        replace: true,
      });
    }
  }, [isLocked, isSessionLockLoading, navigate, isDA]);

  useEffect(() => {
    dispatch(setRawRooms(selectedRooms));
  }, [dispatch, selectedRooms]);

  useEffect(() => {
    dispatch(setNoOfTravellers(Number(travellerCountDropdown?.value)));
  }, [dispatch, travellerCountDropdown]);

  useEffect(() => {
    const tempDefault = roomsState
      .filter((item) => item.type === 'room')
      .map((item) => item.data);
    setDefaultRooms(tempDefault);
  }, [roomsState]);

  return (
    <div
      ref={stageRef}
      className="bg-light-600 md:border border-light-800 md:shadow-[0px_5px_5px_0px_#17181a0d] rounded-md"
    >
      <RoomForm
        minCompanion={
          tripData && tripData.result.trip.min_companion
            ? tripData?.result.trip.min_companion
            : 1
        }
        maxCompanion={
          tripData && tripData.result.trip.max_companion
            ? tripData.result.trip.max_companion
            : 8
        }
        isLoading={tripDataLoading}
        travellerDropdown={travellerCountDropdown}
        setTravellerDropdown={setTravellerCountDropdown}
        rooms={tripData?.result.rooms}
        setSelectedRooms={setSelectedRooms}
        selectedRooms={selectedRooms}
        defaultRoom={defaultRooms}
        onChangeTravellerDropdown={onChangeTravellerDropdown}
      />
      <RoomValidation
        type={roomHasError ? 'error' : 'success'}
        message={roomValidationMessage}
        hideLabel={hideValidationLabel}
        className={isDA ? 'hidden md:block' : ''}
      />
      <div className="hidden md:block">
        <hr className="border-none h-px w-full bg-light-800" />
        <RoomSubmit
          clickHandler={() => checkSubmit()}
          isDisabled={
            roomHasError || isSubmitRoomLoading || isGotToGoProcessing
          }
          isLoading={isSubmitRoomLoading}
          submitButtonText={hasDepartureDate ? 'Continue' : 'Set Date'}
          backHandler={
            isDA ? () => navigate(stagePathConfig.traveller) : undefined
          }
        />
      </div>

      {isMobile && (
        <div
          data-testid="mobile-cta-bar"
          className="fixed z-50 bottom-0 left-0 right-0 bg-light-600"
        >
          {isDA && (
            <RoomValidation
              compact
              type={roomHasError ? 'error' : 'success'}
              message={roomValidationMessage}
              hideLabel={hideValidationLabel}
            />
          )}
          <ContinueCta
            submitHandler={() => checkSubmit()}
            isDisabled={
              roomHasError || isSubmitRoomLoading || isGotToGoProcessing
            }
            isLoading={isSubmitRoomLoading}
            submitButtonText={hasDepartureDate ? 'Continue' : 'Set Date'}
          />
        </div>
      )}
      <ChangeDateModal
        isOpen={isChooseDateOpen}
        close={closeChooseDateModal}
        afterSubmit={afterSubmitModal}
      />
    </div>
  );
};

export { RoomStage };
